import '@/App.css';
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import MainLayout from './components/MainLayout';
import LoginPage from './pages/LoginPage';
import VerifyPasswordPage from './pages/VerifyPasswordPage';
import CompanyManagerPage from './pages/CompanyManagerPage';
import DashboardPage from './pages/DashboardPage';
import CustomersPage from './pages/CustomersPage';
import MachinesPage from './pages/MachinesPage';
import CatalogPage from './pages/CatalogPage';
import ConsumablesPage from './pages/ConsumablesPage';
import ReadingsPage from './pages/ReadingsPage';
import HistoryPage from './pages/HistoryPage';
import PendingPage from './pages/PendingPage';
import ReportsPage from './pages/ReportsPage';
import BackupPage from './pages/BackupPage';
import DispatchesPage from './pages/DispatchesPage';
import ServicePage from './pages/ServicePage';
import BulkReadingsPage from './pages/BulkReadingsPage';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import FlashOverlay, { flashSuccess, flashError } from './components/FlashOverlay';

// Monkey-patch sonner toast to ALSO trigger global FlashOverlay,
// so every toast.success / toast.error / toast.warning shows the centered animation.
// This avoids editing 169 individual toast call-sites.
(function installFlashHooks() {
  if (toast && !toast.__flashPatched) {
    const originalSuccess = toast.success?.bind(toast);
    const originalError   = toast.error?.bind(toast);
    const originalWarning = toast.warning?.bind(toast);
    if (originalSuccess) {
      toast.success = (msg, opts) => { try { flashSuccess(typeof msg === 'string' ? msg : ''); } catch(e) {} return originalSuccess(msg, opts); };
    }
    if (originalError) {
      toast.error = (msg, opts) => { try { flashError(typeof msg === 'string' ? msg : ''); } catch(e) {} return originalError(msg, opts); };
    }
    if (originalWarning) {
      toast.warning = (msg, opts) => { try { flashError(typeof msg === 'string' ? msg : ''); } catch(e) {} return originalWarning(msg, opts); };
    }
    toast.__flashPatched = true;
  }
})();

const ProtectedRoute = ({ children, requireCompany = true, requireVerified = true }) => {
  const { token, loading, companyId, userVerified, role } = useAuth();

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>;
  if (!token) return <Navigate to="/login" />;

  // Super admin without company goes to company manager
  if (role === 'super_admin' && !companyId && requireCompany) return <Navigate to="/companies" />;

  // Company user needs verification
  if (role === 'company' && !userVerified && requireVerified) return <Navigate to="/verify" />;

  return children;
};

const AppRoutes = () => {
  const { token, role, companyId, userVerified, loading } = useAuth();

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>;

  return (
    <Routes>
      <Route path="/login" element={!token ? <LoginPage /> : <Navigate to={role === 'super_admin' && !companyId ? '/companies' : (role === 'company' && !userVerified ? '/verify' : '/dashboard')} />} />

      <Route path="/verify" element={token && role === 'company' && !userVerified ? <VerifyPasswordPage /> : <Navigate to={token ? '/dashboard' : '/login'} />} />

      <Route path="/companies" element={token && role === 'super_admin' ? <CompanyManagerPage /> : <Navigate to="/login" />} />

      {/* All protected app routes */}
      {[
        { path: '/dashboard', Component: DashboardPage },
        { path: '/customers', Component: CustomersPage },
        { path: '/machines', Component: MachinesPage },
        { path: '/catalog', Component: CatalogPage },
        { path: '/consumables', Component: ConsumablesPage },
        { path: '/readings', Component: ReadingsPage },
        { path: '/history', Component: HistoryPage },
        { path: '/pending', Component: PendingPage },
        { path: '/reports', Component: ReportsPage },
        { path: '/backup', Component: BackupPage },
        { path: '/dispatches', Component: DispatchesPage },
        { path: '/service', Component: ServicePage },
        { path: '/bulk-readings', Component: BulkReadingsPage },
      ].map(({ path, Component }) => (
        <Route key={path} path={path} element={
          <ProtectedRoute>
            <MainLayout><Component /></MainLayout>
          </ProtectedRoute>
        } />
      ))}

      <Route path="*" element={<Navigate to="/login" />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

function AppContent() {
  const { theme } = useTheme();
  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'dark bg-gray-900' : ''}`}>
      <AppRoutes />
      <Toaster richColors position="top-center" />
      <FlashOverlay />
    </div>
  );
}

export default App;
